import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { DetrazioniCard } from '../components/Detrazioni/DetrazioniCard';
import {
  calcolaDetrazioni,
  formatCurrency,
  DETRAZIONI_DISPONIBILI,
  type DetrazioniSummary
} from '../services/detrazioniCalculations';
import type { Preventivo } from '../types';

export function Detrazioni() {
  const { currentProject } = useProjectContext();

  // Data states - TODO: Migrate to REST API hooks
  const [preventivi] = useState<Preventivo[]>([]);
  const [isLoading] = useState(false);
  const [error] = useState<string | null>(null);

  // Settings
  const [estimateLevel, setEstimateLevel] = useState<'min' | 'med' | 'max'>('med');
  const [activeBonus, setActiveBonus] = useState<Set<string>>(
    new Set(DETRAZIONI_DISPONIBILI.filter(d => d.attivo).map(d => d.id))
  );

  // Toggle bonus
  const toggleBonus = (bonusId: string) => {
    setActiveBonus(prev => {
      const next = new Set(prev);
      if (next.has(bonusId)) {
        next.delete(bonusId);
      } else {
        next.add(bonusId);
      }
      return next;
    });
  };

  // Calculate detrazioni
  const summary: DetrazioniSummary | null = useMemo(() => {
    if (preventivi.length === 0) return null;

    const configsAttive = DETRAZIONI_DISPONIBILI.filter(c => activeBonus.has(c.id));
    return calcolaDetrazioni(preventivi, estimateLevel, configsAttive);
  }, [preventivi, estimateLevel, activeBonus]);

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per visualizzare le detrazioni</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Detrazioni Fiscali</h2>
          <p className="text-gray-600 mt-1">
            Calcola i bonus edilizi applicabili ai tuoi preventivi
          </p>
        </div>

        {/* Estimate level selector */}
      </div>

      {!currentProject?.immobile?.renditaCatastale && !currentProject?.immobile?.classeEnergetica && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800">
            Compila rendita catastale o classe energetica nella Scheda Immobile per detrazioni accurate
          </p>
          <Link
            to="/immobile"
            className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
          >
            Vai alla Scheda Immobile
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div></div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Stima:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(['min', 'med', 'max'] as const).map((level) => (
              <button
                key={level}
                onClick={() => setEstimateLevel(level)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  estimateLevel === level
                    ? 'bg-white shadow text-primary-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {level === 'min' ? 'Minima' : level === 'med' ? 'Media' : 'Massima'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Bonus selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Bonus attivi</h3>
        <div className="flex flex-wrap gap-2">
          {DETRAZIONI_DISPONIBILI.map(bonus => (
            <button
              key={bonus.id}
              onClick={() => toggleBonus(bonus.id)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                activeBonus.has(bonus.id)
                  ? 'bg-primary-100 border-primary-300 text-primary-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              {bonus.nome} ({bonus.percentuale}%)
            </button>
          ))}
        </div>
      </div>

      {preventivi.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-2">Nessun preventivo presente</p>
          <p className="text-sm text-gray-400">
            Aggiungi voci ai preventivi per calcolare le detrazioni applicabili
          </p>
        </div>
      ) : summary ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Spese totali ammesse</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary.totaleSpese)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Detrazione totale</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.totaleDetrazioni)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Detrazione annua media</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.detrazioneAnnua)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Durata detrazioni</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.anni} anni
              </p>
            </div>
          </div>

          {/* Detrazioni list */}
          {summary.detrazioni.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {summary.detrazioni.map(det => (
                <DetrazioniCard key={det.config.id} detrazione={det} />
              ))}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-700">
                Nessuna spesa corrisponde ai bonus attivi.
                Verifica le categorie dei tuoi preventivi.
              </p>
            </div>
          )}

          {/* Non-matched preventivi */}
          {(() => {
            const matchedIds = new Set(summary.detrazioni.flatMap(d => d.preventivi.map(p => p.id)));
            const unmatched = preventivi.filter(p => !matchedIds.has(p.id));

            if (unmatched.length === 0) return null;

            return (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Voci non detraibili ({unmatched.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {unmatched.map(p => (
                    <span
                      key={p.id}
                      className="text-xs bg-white px-2 py-1 rounded border text-gray-500"
                    >
                      {p.categoria}: {p.voce}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      ) : null}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Come funziona</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Le detrazioni sono calcolate automaticamente in base alle categorie dei preventivi</li>
          <li>• Ogni spesa viene assegnata al bonus più vantaggioso (no doppi conteggi)</li>
          <li>• I massimali sono applicati per singolo bonus</li>
          <li>• Consulta un commercialista per la corretta applicazione delle agevolazioni</li>
        </ul>
      </div>
    </div>
  );
}
